import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Save, Eye, EyeOff } from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';
import { RichText } from '../RichText';
import HistoryDialog from './HistoryDialog';
import UpdatedAtBadge from './UpdatedAtBadge';
import DraftBanner from './DraftBanner';
import { useAutoSaveDraft } from '../../hooks/useAutoSaveDraft';

const defaultData = {
  headline: 'Empowering communities since 1979',
  description:
    'The Education & Welfare Research Foundation (EWRF) is a registered NGO dedicated to uplifting underserved communities in Malaysia through education, counselling, welfare, and volunteer development.',
  vision:
    'A just and compassionate society where every individual, regardless of background, has access to quality education, emotional support, and the resources to thrive.',
  mission:
    'To empower underserved communities through sustainable programmes in education, counselling, and welfare – delivered by trained volunteers and guided by research and compassion.',
  heritage:
    'Founded in 1979 in Petaling Jaya, EWRF has grown from a small welfare group into a nationally recognised organisation touching over 15,000 lives across Malaysia.',
  quote:
    'The true measure of a society is how it treats its most vulnerable members. For 47 years, EWRF has answered that measure with action.',
};

const AboutManager = () => {
  const [data, setData] = useState(defaultData);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi.get('/about');
        if (res.data && res.data.id) setData({ ...defaultData, ...res.data });
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const refetch = async () => {
    const res = await adminApi.get('/about');
    if (res.data && res.data.id) setData({ ...defaultData, ...res.data });
  };

  const { hasDraft, draftAt, readDraft, discardDraft, markSaved } = useAutoSaveDraft({
    storageKey: 'draft:about_main',
    data,
    enabled: loaded,
    savedAt: data.updated_at,
  });

  const restoreDraft = () => {
    const draft = readDraft();
    if (draft) setData({ ...defaultData, ...draft });
    discardDraft();
  };

  const save = async () => {
    if (!loaded) {
      toast({ title: 'Please wait', description: 'Content is still loading — try again in a moment.' });
      return;
    }
    setLoading(true);
    try {
      await adminApi.post('/about', data);
      await refetch();
      markSaved(data);
      toast({ title: 'Saved', description: 'About section updated' });
    } catch (e) {
      toast({ title: 'Error', description: 'Save failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="about-manager">
      {hasDraft && (
        <DraftBanner
          draftAt={draftAt}
          onRestore={restoreDraft}
          onDiscard={discardDraft}
          testid="about-draft-banner"
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-2xl font-bold">About Section</h2>
          <UpdatedAtBadge updatedAt={data.updated_at} />
        </div>
        <div className="flex items-center gap-2">
          <HistoryDialog collection="about_content" docId="about_main" onRestored={refetch} />
          <Button
            variant="outline"
            onClick={() => setShowPreview((s) => !s)}
            data-testid="about-preview-toggle"
          >
            {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {showPreview ? 'Hide Preview' : 'Live Preview'}
          </Button>
        </div>
      </div>

      <div className={`grid ${showPreview ? 'lg:grid-cols-2' : ''} gap-6`}>
      <Card>
        <CardHeader><CardTitle>Edit Content</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="about-headline">Headline</Label>
            <Input id="about-headline" value={data.headline} onChange={(e) => setData({ ...data, headline: e.target.value })} data-testid="about-headline-input" />
          </div>
          <div>
            <Label>Description</Label>
            <RichTextEditor value={data.description} onChange={(v) => setData({ ...data, description: v })} placeholder="Description..." />
          </div>
          <div>
            <Label>Pull Quote</Label>
            <RichTextEditor value={data.quote} onChange={(v) => setData({ ...data, quote: v })} minHeight={90} />
          </div>
          <div>
            <Label>Vision</Label>
            <RichTextEditor value={data.vision} onChange={(v) => setData({ ...data, vision: v })} />
          </div>
          <div>
            <Label>Mission</Label>
            <RichTextEditor value={data.mission} onChange={(v) => setData({ ...data, mission: v })} />
          </div>
          <div>
            <Label>Heritage</Label>
            <RichTextEditor value={data.heritage} onChange={(v) => setData({ ...data, heritage: v })} />
          </div>
          <Button onClick={save} disabled={loading || !loaded} className="bg-[#1565C0]" data-testid="save-about-btn">
            <Save className="w-4 h-4 mr-2" />{!loaded ? 'Loading…' : loading ? 'Saving…' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>

      {showPreview && (
        <Card data-testid="about-preview">
          <CardHeader><CardTitle>Live Preview</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <h3 className="text-2xl font-serif text-[#0A1628]">{data.headline}</h3>
            <RichText html={data.description} />
            {data.quote && (
              <blockquote className="border-l-4 border-[#D4AF37] pl-4 italic text-gray-600">
                <RichText html={data.quote} />
              </blockquote>
            )}
            <div>
              <h4 className="font-semibold text-[#0A1628]">Vision</h4>
              <RichText html={data.vision} className="text-gray-600 text-sm" />
            </div>
            <div>
              <h4 className="font-semibold text-[#0A1628]">Mission</h4>
              <RichText html={data.mission} className="text-gray-600 text-sm" />
            </div>
            <div>
              <h4 className="font-semibold text-[#0A1628]">Heritage</h4>
              <RichText html={data.heritage} className="text-gray-600 text-sm" />
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
};

export default AboutManager;
